import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useQuerySelectorVisibility } from "@/hooks/useElementVisibility";
import { trackEvent, trackConversionFunnel } from "@/components/GoogleAnalytics";
import { trackEvent as trackEventHj, trackConversionFunnelHj } from "@/components/Hotjar";
import { trackEventMp, trackConversionFunnelMp } from "@/components/Mixpanel";
import { useTranslation } from "react-i18next";

interface StickySubscribeButtonProps {
  onSubscribe: () => void;
}

export function StickySubscribeButton({ onSubscribe }: StickySubscribeButtonProps) {
  const { t } = useTranslation();
  const [showButton, setShowButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Monitor visibility of both CTA buttons and footer contacts
  const isPrimaryCtaVisible = useQuerySelectorVisibility('#primary-cta-button', { threshold: 0.1 });
  const isNewsletterFormVisible = useQuerySelectorVisibility('#newsletter-form-button', { threshold: 0.1 });
  const isFooterContactsVisible = useQuerySelectorVisibility('#footer-contacts', { threshold: 0.1 });
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    // Add a small delay to prevent flicker on page load
    const timer = setTimeout(() => {
      // Show sticky button only when BOTH main CTAs are not visible
      // AND hide it when footer contacts are visible
      const shouldShow = !isPrimaryCtaVisible && !isNewsletterFormVisible && !isFooterContactsVisible;
      setShowButton(shouldShow);
    }, 500);

    return () => clearTimeout(timer);
  }, [isPrimaryCtaVisible, isNewsletterFormVisible, isFooterContactsVisible, isMobile]);

  const handleClick = () => {
    // Track sticky button click
    trackEvent('sticky_button_clicked', {
      event_category: 'engagement',
      event_label: 'sticky_subscribe_button',
      button_position: isMobile ? 'mobile_bottom' : 'desktop_center'
    });
    trackEventMp('sticky_button_clicked', {
      event_category: 'engagement',
      event_label: 'sticky_subscribe_button',
      button_position: isMobile ? 'mobile_bottom' : 'desktop_center'
    });
    trackEventHj('sticky_button_clicked');
    trackConversionFunnel('sticky_button_clicked', 2);
    trackConversionFunnelHj('sticky_button_clicked', 2);
    trackConversionFunnelMp('sticky_button_clicked', 2);
    
    // Smooth scroll to newsletter form
    const formElement = document.querySelector('#newsletter-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    onSubscribe();
  };

  return (
    <div
      className={cn(
        "fixed z-[9999] transition-all ease-out duration-500",
        "bottom-4 left-4 right-4 md:bottom-8 md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-auto",
        showButton ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none translate-y-full"
      )}
    >
      <Button
        id="sticky-subscribe-button"
        onClick={handleClick}
        className={cn(
          "group relative overflow-hidden w-full md:w-auto",
          "text-white",
          "border-2 border-white/20 hover:border-white/40",
          "px-8 md:px-12 py-4 md:py-3 rounded-2xl",
          "font-semibold text-base md:text-sm",
          "transition-all",
          "hover:scale-105 hover:-translate-y-1",
          "animate-smooth-pulse hover:animate-none",
          "flex items-center justify-center gap-2 min-h-[44px]"
        )}
        style={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #FF8F65 50%, #FFB085 100%)',
          boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)',
          touchAction: 'manipulation'
        }}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
        
        <Mail className="w-4 h-4 flex-shrink-0" />
        <span>{t('stickyButton.subscribeText')}</span>
      </Button>
    </div>
  );
}